import React, { useEffect, useState } from "react";
import { Grid, Button, Backdrop, CircularProgress } from "@mui/material";
import { useForm } from "react-hook-form";
import ReusableModal from "../../Components/Modal/WSModal";
import WSSelectField from "../../Components/Select/WSSelect";
import WSTextField from "../../Components/TextBox/WSTextBox";
import WSTextAreaField from "../../Components/WSTextArea/WSTextArea";
import {
  callAPI,
  customValidation,
  Method,
  validations
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";

interface ModalProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  onCreate: (data: any) => void;
}

const ClientModal: React.FC<ModalProps> = ({
  id,
  open,
  onClose,
  onCreate
}) => {
  const { showMessage } = useSnackbar();
  const { control, handleSubmit, setValue,reset } = useForm();
  const [loading, setLoading] = useState(false);

  const clientSource = {
    url: "rateCards/search",
    pageSize: 100000,
    page: 1,
    filter: [],
    sort: []
  };

  useEffect(() => {
    const fetchDataById = async () => {
      setLoading(true);
      try {
        const response: any = await callAPI(
          "clients/getBy",
          Method.GET,
          {},
          id
        );

        const data = response?.client;

        setValue("name", data.name);
        setValue("email", data.email);
        setValue("phone", data.phone);
        setValue("address", data.address);
        setValue("note", data.note);
        setValue("rateCard", data?.rateCard._id);
        setValue("company", data.company);
      } catch (error: any) {
        if (error.message === 'GlobalHandledError') return;
        showMessage(
          error.response?.data?.message || "Failed to retrieve account data",
          "error"
        );
        console.error("Failed to retrieve account data", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDataById();
  }, [id,setValue]);

  const onSubmit = async (data: any) => {
    onCreate(data);
    reset();
  };

  const handleTransformData = (data: any) => {
    let allData: Array<any> = [];
    data.map((value: any) => {
      allData.push({ label: value.name, value: value._id });
    });

    return allData;
  };

  const footer = (
    <Button
      variant="contained"
      onClick={handleSubmit(onSubmit)}
      sx={{
        backgroundColor: "#FF6B2C",
        "&:hover": { backgroundColor: "#e65c1d" }
      }}
    >
      Create
    </Button>
  );

  return (
    <>
      {loading ? (
        <Backdrop
          open={true}
          sx={{ zIndex: 1301, color: "#fff", backdropFilter: "blur(5px)" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <ReusableModal
          open={open}
          onClose={onClose}
          title="New Client"
          footer={footer}
          width="sm"
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <WSTextField
                id="clientName"
                label="Client Name"
                name="name"
                control={control}
                rules={{ required: "client name is required" }}
                sx={{
                  marginBottom: 1,
                  backgroundColor: "rgba(0,0,0,.05)",
                  boxShadow: "none",
                  border: 0,
                  color: "#444",
                  fontSize: 16,
                  transition: "width 0.25s"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <WSTextField
                id="emailID"
                label="Email Address"
                name="email"
                control={control}
                rules={customValidation(true, validations.email)}
                sx={{
                  marginBottom: 1,
                  backgroundColor: "rgba(0,0,0,.05)",
                  boxShadow: "none",
                  border: 0,
                  color: "#444",
                  fontSize: 16,
                  transition: "width 0.25s"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <WSTextField
                id="phone"
                label="Phone Number"
                name="phone"
                control={control}
                rules={customValidation(true, validations.phone)}
                sx={{
                  marginBottom: 1,
                  backgroundColor: "rgba(0,0,0,.05)",
                  boxShadow: "none",
                  border: 0,
                  color: "#444",
                  fontSize: 16,
                  transition: "width 0.25s"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <WSTextField
                id="company"
                label="Company Name"
                name="company"
                control={control}
                sx={{
                  marginBottom: 1,
                  backgroundColor: "rgba(0,0,0,.05)",
                  boxShadow: "none",
                  border: 0,
                  color: "#444",
                  fontSize: 16,
                  transition: "width 0.25s"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <WSTextField
                id="address"
                label="Address"
                name="address"
                control={control}
                sx={{
                  marginBottom: 1,
                  backgroundColor: "rgba(0,0,0,.05)",
                  boxShadow: "none",
                  border: 0,
                  color: "#444",
                  fontSize: 16,
                  transition: "width 0.25s"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <WSTextAreaField
                id="note"
                label="Note"
                name="note"
                control={control}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <WSSelectField
                id="rateCard"
                label={"Select Rate Card"}
                name="rateCard"
                control={control}
                source={clientSource}
                rules={{ required: "please select a rate card" }}
                transFormData={handleTransformData}
              />
            </Grid>
          </Grid>
        </ReusableModal>
      )}
    </>
  );
};

export default ClientModal;
